import { Button, Flex, type FlexProps, InputGroup, Text } from '@chakra-ui/react'
import { BaseInput, EmberMessage, ExampleQuestions, FlexCol, FlexRow, UserMessage } from '@components'
import { SimpleEmberLoadProfile } from '@features/Ember/visualizations/SimpleEmberLoadProfile'
import { WeekdaysCard } from '@features/profile/presentation/screens/Graphs/WeekDays/WeekdaysCard'
import { InsightsCard } from '@widgets/InsightsVideoCta/InsightsList'
import { type FC, Fragment, useEffect, useRef, useState } from 'react'
import { Send } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { FaRegCircleStop } from 'react-icons/fa6'
import { EmberActionButton, type IProposedUserAction } from './EmberActionButton'
import type { IEmberMessage } from './types'

/* Internal sub-components */

const ConversationStartMessage: FC = () => {
  const { t } = useTranslation()
  return (
    <Flex m='auto' flexGrow={'1'} alignItems={'center'}>
      <Text color='#8DBED1' fontSize={'16px'} fontWeight={500}>
        {t('emberChat.how_can_i_help_you')}
      </Text>
    </Flex>
  )
}

const ChatAreaMessagesDisplay: FC<Pick<EmberChatAreaUIProps, 'insight' | 'messages' | 'thinkingExplanation' | 'proposedQuestions'>> = ({
  insight,
  messages,
  thinkingExplanation,
  proposedQuestions,
}) => {
  const chatAreaRef = useRef<HTMLDivElement>(null)
  const [isAtBottom, setIsAtBottom] = useState(true)

  // Update isAtBottom state based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (!chatAreaRef.current) return
      const { scrollHeight, scrollTop, clientHeight } = chatAreaRef.current
      setIsAtBottom(scrollHeight - scrollTop <= clientHeight + 10)
    }

    const chatArea = chatAreaRef.current
    if (chatArea) chatArea.addEventListener('scroll', handleScroll)

    return () => {
      if (chatArea) chatArea.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Scroll to bottom when messages or proposed questions change
  useEffect(() => {
    if (isAtBottom && chatAreaRef.current) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight
    }
  }, [messages, proposedQuestions, isAtBottom])

  return (
    <FlexCol ref={chatAreaRef} gap='20px' overflowY='auto' className={'innerScrollBar'} flexBasis={'content'} flexGrow={'1'}>
      {insight && <InsightsCard insight={insight} cardsBgColor={'rgba(255,255,255,0.7)'} inEmberChat />}

      {messages.length === 0 && <ConversationStartMessage />}

      {messages.map((message, idx) => {
        const { role, displayValue, isThinking, visualization } = message

        const key = `msg-${role}-${idx}`

        if (role === 'user') {
          return <UserMessage bgColor={'#F3F3F3'} key={key} text={displayValue ?? ''} />
        }

        if (role === 'ember') {
          const showExplanation = isThinking && thinkingExplanation

          return (
            <Fragment key={key}>
              <EmberMessage
                text={showExplanation ? thinkingExplanation : displayValue}
                isThinking={isThinking}
                isThinkingExplanation={!!showExplanation}
              />

              {visualization?.type === 'load_profile' && visualization?.data?.entries?.length > 0 && (
                <SimpleEmberLoadProfile entries={visualization.data.entries} />
              )}
              {visualization?.data && visualization?.type === 'day_bar_chart' && (
                <WeekdaysCard graphData={visualization?.data.entries} width='100%' />
              )}
              {/* NOTE: currently not supported by Ember v2 */}
              {/* {visualization?.data && visualization?.type === 'series_comparisons' && (
                  <Ember2LineChart
                    entries1={visualization.data.line1Data}
                    entries2={visualization.data.line2Data}
                    line1Description={visualization.metadata.line1Description}
                    line2Description={visualization.metadata.line2Description}
                    line1Legend={visualization.metadata.line1Legend}
                    line2Legend={visualization.metadata.line2Legend}
                    line1Label={visualization.metadata.line1Label}
                    line2Label={visualization.metadata.line2Label}
                  />
                )} */}
            </Fragment>
          )
        }
      })}
    </FlexCol>
  )
}

const ChatInput: FC<
  Pick<EmberChatAreaUIProps, 'onSend' | 'onCancel' | 'onInputChange' | 'value' | 'isLoading'> & { isThinking: boolean }
> = ({ value, onInputChange, onSend, onCancel, isLoading, isThinking }) => {
  const { t } = useTranslation()
  const isBusy = isLoading || isThinking

  const chatInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (!isBusy) {
      chatInputRef.current?.focus()
    }
  }, [isBusy])

  return (
    <InputGroup>
      <BaseInput
        ref={chatInputRef}
        disabled={isBusy}
        height={'41px'}
        ButtonRight={
          !isBusy ? (
            <Button onClick={() => onSend?.()} padding={'0px'} borderWidth={0} size='sm'>
              <Send color='#5472AB' />
            </Button>
          ) : (
            <Button onClick={onCancel} padding={'0px'} borderWidth={0} size='sm'>
              <FaRegCircleStop size={'24px'} color='#5472AB' />
            </Button>
          )
        }
        onKeyDown={e => {
          if (e.nativeEvent.key === 'Enter') {
            onSend?.()
          }
        }}
        onChange={e => {
          onInputChange?.(e.target.value)
        }}
        value={value || ''}
        fontSize={'14px'}
        placeholder={isThinking ? '' : t('emberChat.ask_question')}
      />
    </InputGroup>
  )
}

/* Exported ChatAreaUI component */

interface EmberChatAreaUIProps {
  messages: IEmberMessage[]
  thinkingExplanation?: string | null
  proposedUserAction?: IProposedUserAction | null
  isLoading?: boolean
  value?: string
  proposedQuestions?: string[]
  onSend?: (question?: string) => void
  onCancel?: () => void
  insight?: any
  onInputChange?: (value: string) => void
  containerProps?: FlexProps
}

export const EmberChatAreaUI: FC<EmberChatAreaUIProps> = props => {
  const { messages = [], isLoading, proposedQuestions, onSend, containerProps, proposedUserAction } = props
  const isThinking = (messages.length > 0 && messages[messages.length - 1].isThinking) ?? false

  const { t } = useTranslation()

  return (
    <FlexCol
      maxH='100%'
      width={'100%'}
      paddingX={'8px'}
      overflowX='hidden'
      justifyContent={'space-between'}
      gap={'10px'}
      {...containerProps}
    >
      <ChatAreaMessagesDisplay {...props} />

      <FlexCol flex='none' gap='16px' paddingX='8px' mb={'20px'} overflowY={'auto'}>
        {/* If a user action should be proposed, show the action button */}
        {proposedUserAction?.readyToShow && <EmberActionButton action={proposedUserAction.action} />}

        {/* Show example question buttons a user could trigger by clicking them */}
        <ExampleQuestions onClick={onSend} questions={proposedQuestions || []} disabled={!!isLoading || !!isThinking} />

        {/* Show main chat input field */}
        <FlexCol gap='6px'>
          <ChatInput {...props} isThinking={isThinking} />
          <FlexRow justifyContent={'center'}>
            <Text color='#B4B4B4' fontSize={'10px'}>
              {t('emberChat.think_before_you_believe_AI_results')}
            </Text>
          </FlexRow>
        </FlexCol>
      </FlexCol>
    </FlexCol>
  )
}
